import { BaseTable, Card, DashboardFilter, HeaderMenu, Loading, TableHeader, } from "components"
import { ExpensesActivites, InfoSection, PayableTable, StatementInfo } from "./components"
import moment from "moment"
import { convertNumber } from "utils"
import { useBalancesheetStatementList, useCashFlowShortermList, useCashFlowStatementList, useExpencesActivitiesListFixed, useExpencesActivitiesListOther, useExpencesActivitiesListVariable, usePayableList, usePNLStatementList, useReceivableList } from "./utils"
import { FormType } from "../../../../components/widgets/DashboardFilter/utils"

export default function Dashboard() {
  const yearMonthFormat = (dateString: string) => {
    const [year, month] = dateString.split("-");
    return `${year}${month}`;
  };
  
  /** Parameters */
  const paramsList = {
    date_from: moment().startOf("month").format("YYYY-MM-DD"), 
    date_end: moment().endOf("month").format("YYYY-MM-DD"),
    location_id: "",
    type: "Month"
  }
  const paramsStatement = {
    date_start: moment().startOf("month").format("YYYY-MM-DD"), 
    location_id: ""
  }
  const paramsMonth = {
    year_month: yearMonthFormat(moment().startOf("month").format("YYYY-MM-DD")), 
    location_id: ""
  }

  /** Hooks */
  const receivableList = useReceivableList(paramsList) 
  const payableList = usePayableList(paramsList)
  const cashFlowStatementList = useCashFlowStatementList(paramsStatement)
  const pnlStatementList = usePNLStatementList(paramsStatement)
  const balancesheetStatementList = useBalancesheetStatementList(paramsStatement)
  const cashFlowShortermList = useCashFlowShortermList(paramsMonth)
  const expencesActivitiesListFixed = useExpencesActivitiesListFixed(paramsMonth)
  const expencesActivitiesListVariable = useExpencesActivitiesListVariable(paramsMonth)
  const expencesActivitiesListOther = useExpencesActivitiesListOther(paramsMonth)

  return (
    <section className="container my-5">
      <section className="flex flex-col gap-3">
        <HeaderMenu title="FINANCE DASHBOARD" />
        <DashboardFilter 
          thirtyDatesMaximum 
          onChangeFilter={(value: FormType) => {
            const dataFilter = { date_start: value.date_from, location_id: value.location_id }
            const agedFilter = { date_from: value.date_from, date_end: value.date_end, location_id: value.location_id, type:value.type}
            const dataMonth = { year_month: yearMonthFormat(value.date_from), location_id: value.location_id }

            receivableList.refetch(agedFilter)
            payableList.refetch(agedFilter)
            cashFlowStatementList.refetch(dataFilter);
            pnlStatementList.refetch(dataFilter);
            balancesheetStatementList.refetch(dataFilter);
            cashFlowShortermList.refetch(dataMonth);
            expencesActivitiesListFixed.refetch(dataMonth);
            expencesActivitiesListVariable.refetch(dataMonth);
            expencesActivitiesListOther.refetch(dataMonth);
          }} 
        />

        <Card>
          <Card.Body>
            <section className="overflow-x-auto max-h-[calc(100vh-500px)]">
              <div>
                <div className="label-text mb-[20px]">
                  <section>
                    <b>ACCOUNT RECEIVABLE</b>

                    <div className="lg:w-[45%] ml-5">
                      <InfoSection
                        className="!justify-start"
                        labelClass="lg:ml-5"
                        label="Turnover Ratio"
                        value="-"
                      />
                    </div>

                    <BaseTable>
                      <TableHeader>
                        <tr>
                          <th>NO</th>
                          <th>INVOICE ID#</th>
                          <th>REF SO ID#</th>
                          <th>DATE</th>
                          <th>CUSTOMER</th>
                          <th>BALANCE</th>
                          <th>7 DAYS</th>
                          <th>15 DAYS</th>
                          <th>21 DAYS</th>
                          <th>30 DAYS</th>
                          <th>60+ DAYS</th>
                        </tr>
                      </TableHeader>
                      <tbody>
                        {receivableList.isLoading || !receivableList.data.length ? (
                          <tr>
                            <td colSpan={11}>
                            {/* <td colSpan={12}> */}
                              <Loading
                                errorText="No data available"
                                loading={receivableList.isLoading}
                              />
                            </td>
                          </tr>
                        ) : receivableList.data.filter(item => item.balance !== 0).map((item, key) => {
                          // Functions
                          const getBalanceByAge = ( num1: number, num2?: number ): string => {
                            // Vars
                            let result = ""

                            if (num2) {
                              if (item.aged_receivable >= num1 && item.aged_receivable <= num2) {
                                result = convertNumber(item.balance).intoCurrency
                              }
                            } else {
                              if (item.aged_receivable >= num1) {
                                result = convertNumber(item.balance).intoCurrency
                              }
                            }

                            return result
                          }

                          return (
                            <tr key={key}>
                              <td className="w-[50px]">{key + 1}</td>
                              <td className="w-[100px]">{item.trx_code}</td>
                              <td className="w-[100px]">{item.sales_order.trx_code}</td>
                              <td>{moment(item.transaction_date).format("DD MMM YYYY")}</td>
                              <td className="w-[200px]">{item.customer.customer_name}</td>
                              <td className="w-[200px] text-right">{convertNumber(item.balance).intoCurrency}</td>
                              <td className="text-right">{getBalanceByAge(1, 7)}</td>
                              <td className="text-right">{getBalanceByAge(8, 15)}</td>
                              <td className="text-right">{getBalanceByAge(16, 21)}</td>
                              <td className="text-right">{getBalanceByAge(22, 30)}</td>
                              <td className="text-right">{getBalanceByAge(31)}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </BaseTable>
                  </section>

                  <PayableTable data={payableList} />
                </div>

                <StatementInfo 
                  dataCashFlow={cashFlowStatementList?.data} 
                  dataPNL={pnlStatementList?.data} 
                  dataBalanceSheet={balancesheetStatementList?.data}
                  dataCashFlowShorterm={cashFlowShortermList?.data}
                />
              </div>

              <hr className="mb-[40px] mt-5" />

              <ExpensesActivites 
                fixed={expencesActivitiesListFixed?.data!} 
                variable={expencesActivitiesListVariable?.data!} 
                other={expencesActivitiesListOther?.data!} 
              />
            </section>
          </Card.Body>
        </Card>
      </section>
    </section>
  )
}